import {
  AcademicCapIcon,
  AdjustmentsIcon,
  AnnotationIcon,
  ChartSquareBarIcon,
  LightningBoltIcon,
  SparklesIcon,
} from '@heroicons/react/outline';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';

import { scrollToSection, WORKSHOP_CONTACT_ID } from '@/utils/scrollToSection';

const features = [
  {
    name: 'Business-Centric Approach',
    description:
      'This workshop is curated specifically for busy bodies. It leverages generative AI for business growth, creativity, and operational efficiency.',
    icon: ChartSquareBarIcon, // Analytics/business
  },
  {
    name: 'Hands-On Experience',
    description:
      'Attendees gain exclusive hands-on experience with the Easy-Peasy.AI platform, understanding how it can be specifically applied to their business needs.',
    icon: LightningBoltIcon, // Energy/action
  },
  {
    name: 'Interactive and Fun Elements',
    description: 'WBe ready for some fun and excitement during the workshop.',
    icon: SparklesIcon, // Fun/interactive
  },
  {
    name: 'Innovation Focus',
    description:
      'The workshop emphasizes innovation, showing you how to use AI to create new value propositions and competitive advantages.',
    icon: AnnotationIcon, // Creativity/ideas/innovation
  },
  {
    name: 'Personalized Content',
    description:
      'We are here to streamline your processes and bring your projects to fruition with precision and prowess.',
    icon: AdjustmentsIcon, // Personalization/tuning
  },
  {
    name: 'Expert Insights',
    description:
      'You wll learn from experts successfully implementing AI in various businesses.',
    icon: AcademicCapIcon, // Expert/insight/learning
  },
];

export default function Example() {
  const { t } = useLocale('common');

  return (
    <div className='overflow-hidden bg-white py-32 dark:bg-slate-800'>
      <div className='mx-auto max-w-7xl px-6 lg:flex lg:px-8'>
        <div className='mx-auto grid max-w-2xl grid-cols-1 gap-x-12 gap-y-16 lg:mx-0 lg:min-w-full lg:max-w-none lg:flex-none lg:gap-y-8'>
          <div className='mx-auto max-w-3xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-slate-200 sm:text-4xl'>
              What Makes Us Different?
            </h2>
            <p className='mt-4 text-lg leading-8 text-gray-600 dark:text-slate-400'>
              Everyday we make AI accessible and understandable for everyone by
              creating simple, ethical, and inclusive AI solutions that help
              users achieve their goals.
            </p>
          </div>

          <div className='mx-auto max-w-3xl lg:w-full'>
            <dl className='space-y-10 sm:grid sm:grid-flow-col sm:grid-cols-2 sm:grid-rows-3 sm:gap-x-6 sm:gap-y-10 sm:space-y-0 lg:gap-x-8'>
              {features.map((feature) => (
                <div key={feature.name} className='relative'>
                  <dt>
                    <feature.icon
                      className='absolute h-6 w-6 text-primary-500'
                      aria-hidden='true'
                    />
                    <p className='ml-9 text-lg font-medium leading-6 text-gray-900 dark:text-slate-200'>
                      {t(feature.name)}
                    </p>
                  </dt>
                  <dd className='ml-9 mt-2 text-base text-gray-500 dark:text-slate-400'>
                    {t(feature.description)}
                  </dd>
                </div>
              ))}
            </dl>
          </div>

          <div className='flex flex-col items-center gap-12'>
            <div className='flex justify-center'>
              <a
                href={`#${WORKSHOP_CONTACT_ID}`}
                onClick={scrollToSection}
                className='rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
              >
                I Want To Make AI Work For Me{' '}
                <span aria-hidden='true'>&rarr;</span>
              </a>
            </div>

            <div className='hidden flex-wrap items-start justify-center gap-6 sm:flex sm:gap-8'>
              <div className='flex w-64 flex-none justify-end self-end lg:w-auto'>
                <Image
                  src='https://media.easy-peasy.ai/static/workshop/Workshop3.jpg'
                  alt=''
                  width={400}
                  height={600}
                  className='aspect-[7/4] w-[24rem] max-w-none flex-none rounded-2xl bg-gray-50 object-cover'
                />
              </div>
              <div className='flex w-96 flex-auto justify-end lg:w-auto lg:flex-none'>
                <Image
                  src='https://media.easy-peasy.ai/static/workshop/Workshop1new.jpg'
                  alt=''
                  width={400}
                  height={600}
                  className='aspect-[7/4] w-[32rem] max-w-none flex-none rounded-2xl bg-gray-50 object-cover'
                />
              </div>
              <div className='hidden sm:block sm:w-0 sm:flex-auto lg:w-auto lg:flex-none'>
                <Image
                  src='https://media.easy-peasy.ai/static/workshop/Workshop2new.jpg'
                  alt=''
                  width={400}
                  height={600}
                  className='aspect-[5/3] w-[24rem] max-w-none rounded-2xl bg-gray-50 object-cover'
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
